'use client';

import React, { useEffect, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import { getRooms } from '@/components/blocks/simple-passenger/simple-passenger.utils';
import AlertBanner from '@/components/molecules/alert-banner/alert-banner';
import EditPassengersListModal from '@/components/organisms/edit-passengers-list-modal/edit-passengers-list-modal';
import RoomOccupantsOverview from '@/components/organisms/room-occupants-overview/room-occupants-overview';
import Link from '@/components/utilities/link/link';
import useQuerySimplePassenger from '@/integrations/content/content-modules/simple-passenger/simple-passenger.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import styles from './simple-passenger.module.scss';

export interface SimplePassengerProps {
  /** The text to show as a headline in edit modal*/
  editModalHeadline?: Content.Components['SimplePassengerHeadlessModuleDto']['editModalHeadline'];
  /** The text to show as a subHeadline in edit modal*/
  editModalSubHeadline?: Content.Components['SimplePassengerHeadlessModuleDto']['editModalSubHeadline'];
  /** The text to show as a headline */
  headline?: Content.Components['SimplePassengerHeadlessModuleDto']['headline'];
  /** The UUID of the module */
  id: Content.Components['SimplePassengerHeadlessModuleDto']['id'];
  /** The UUID of the current page */
  pageId: Content.Components['SimplePassengerHeadlessModuleDto']['pageId'];
  /** The parameters for the booking */
  parameters: Content.Components['SimplePassengerHeadlessModuleDto']['parameters'];
  /** The text to show as a subHeadline */
  subHeadline?: Content.Components['SimplePassengerHeadlessModuleDto']['subHeadline'];
}

const SimplePassenger = ({
  editModalHeadline,
  editModalSubHeadline,
  headline,
  id,
  pageId,
  subHeadline,
  parameters,
}: SimplePassengerProps) => {
  const { t } = useI18n();
  const [showEditModal, setShowEditModal] = useState(false);
  const [rooms, setRooms] = useState<Content.Properties.Room[]>([]);

  const identifiers = { ...parameters, id, pageId };

  const {
    data: dataSimplePassenger,
    isFetching: isFetchingSimplePassenger,
    isLoading: isLoadingSimplePassenger,
  } = useQuerySimplePassenger({ parameters: identifiers });

  const alertBannerText =
    dataSimplePassenger && dataSimplePassenger.childrenInfoText;
  const alertBannerLink =
    dataSimplePassenger && dataSimplePassenger.childrenInfoLink;

  const shouldShowAlertBanner = rooms.some(room =>
    room.passengers.some(
      passenger =>
        passenger.gender === 'infant' || passenger.gender === 'child',
    ),
  );
  const hasAlertBanner =
    shouldShowAlertBanner && (alertBannerText || alertBannerLink);

  useEffect(() => {
    if (!dataSimplePassenger) return;

    setRooms(getRooms(dataSimplePassenger));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSimplePassenger]);

  return (
    <div className={styles.component}>
      <div className={styles.headerWrapper}>
        <div className={styles.header}>
          {headline && (
            <Typography className={styles.headline} tagStyle="headingSmall">
              {headline}
            </Typography>
          )}

          <Link className={styles.edit} onClick={() => setShowEditModal(true)}>
            <Typography tagStyle="bodyMedium">
              {t('items.modules.bookingFlow.editNames')}
            </Typography>
          </Link>
        </div>

        {subHeadline && (
          <Typography className={styles.subHeadline} tagStyle="bodyMedium">
            {subHeadline}
          </Typography>
        )}
      </div>

      {isLoadingSimplePassenger && (
        <React.Fragment>
          <RoomOccupantsOverview isLoading />

          <RoomOccupantsOverview isLoading />
        </React.Fragment>
      )}

      {!isLoadingSimplePassenger &&
        rooms.map((room, roomIndex) => {
          return (
            <RoomOccupantsOverview
              key={roomIndex}
              isFetching={isFetchingSimplePassenger}
              passengers={room.passengers}
              roomName={room.roomName}
              roomNumber={roomIndex + 1}
            />
          );
        })}

      {hasAlertBanner && (
        <AlertBanner variant="neutral">
          <Typography tag="span" tagStyle="bodyMedium">
            {alertBannerText}

            {alertBannerLink && (
              <Link className={styles.link} url={alertBannerLink.url}>
                {alertBannerLink.text}
              </Link>
            )}
          </Typography>
        </AlertBanner>
      )}

      <EditPassengersListModal
        headline={editModalHeadline}
        hideAddress
        hideAge
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        parameters={identifiers}
        rooms={rooms}
        subHeadline={editModalSubHeadline}
      />
    </div>
  );
};

SimplePassenger.displayName = 'SimplePassenger';

export default SimplePassenger;
